import "./Leaderboard.css"

import { useMemo, useState } from "react"
import { Col, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"

import Loading from "@/components/Loading/Loading"
import {
  useProfileData,
  useStatsData,
  useStudentRankingData,
} from "@/queries/queries"
import type { HomeRanking, TopStudent } from "@/types/homeTypes"

import BarChart from "../BarCharts/BarCharts"
import YourProgress from "../YourProgress/YourProgress"
import BoardLeaderItem from "./BoardLeaderItem"
import RoundsFilter from "./RoundsFilter"
import RoundStatsCharts from "./RoundStatsChart"

type RequiredTopStudent = Required<TopStudent>
type RankedStudent = RequiredTopStudent & { realRank: number }
type ScaledStudent = RankedStudent & { height: string }

export default function Leaderboard() {
  const { t } = useTranslation()

  const homeRankingQuery = useStudentRankingData()
  const homeStatsData = useStatsData()
  const profileQuery = useProfileData()

  const [currentRound, setCurrentRound] = useState<number>()

  const current = useMemo(() => {
    if (!homeRankingQuery.data?.length || !currentRound) return undefined
    return homeRankingQuery.data.find((ele) => ele.round_id === currentRound)
  }, [homeRankingQuery.data, currentRound])

  const currentStats = useMemo(() => {
    if (!homeStatsData.data?.length || !current?.round_name) return null
    return (
      homeStatsData.data?.find((ele) => ele.round === current.round_name) ||
      null
    )
  }, [homeStatsData.data, current?.round_name])

  const reordered = useRankedReorderedScaledStudents(current)

  if (homeRankingQuery.isLoading) return <Loading />
  if (!homeRankingQuery.data?.length) return null

  return (
    <div className="leaderboard-section-card">
      <h5 className="section-title">{t("homePage.leaderboard.roundRank")}</h5>

      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <RoundsFilter
          data={homeRankingQuery.data}
          setCurrentRound={setCurrentRound}
          currentRound={currentRound}
        />
      </div>

      <div
        className={`con align-items-${
          reordered.length === 0 ? "center" : "end"
        }`}
        style={{ background: "#f8f9fa" }}
      >
        <span className="bg">{homeBackGround}</span>
        {reordered.length === 0 ? (
          <div className="py-5">
            <img
              src="assets/images/eraa.webp"
              style={{ maxWidth: "200px", opacity: 0.5 }}
              alt=""
            />
          </div>
        ) : (
          <div className="bars-wrapper">
            <div className="bars-con">
              {reordered.map((student) => (
                <BoardLeaderItem
                  key={student.student_id}
                  name={student.name}
                  score={student.score}
                  rank={student.realRank}
                  height={student.height}
                  image={student.image}
                  isMe={student.student_id === profileQuery.data?.id}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <Row className="mt-5 g-4">
        <Col lg={12}>
          <div className="p-3 border rounded-4 h-100 bg-light">
            <h6 className="fw-bold mb-3">
              {t("homePage.leaderboard.lecturesDegree")}
            </h6>
            <BarChart />
          </div>
        </Col>
        <Col lg={12}>
          <div className="p-3 border rounded-4 h-100 bg-light">
            <h6 className="fw-bold mb-3">
              {t("homePage.leaderboard.roundStats.title")}
            </h6>
            {currentStats && <RoundStatsCharts stats={currentStats} />}
          </div>
        </Col>

        <Col lg={12}>
          <YourProgress id={currentRound} />
        </Col>
      </Row>
    </div>
  )
}

// const minHeight = 100
// const maxHeight = 145

function useRankedReorderedScaledStudents(current?: HomeRanking) {
  const rankedStudents = useMemo(() => {
    if (!current?.top_students?.length) return [] as Array<RankedStudent>
    return (current.top_students as Array<RequiredTopStudent>)
      .filter((student) => student.score > 80)
      .sort((a, b) => b.score - a.score)
      .map((student, index) => ({
        ...student,
        realRank: index + 1,
      }))
      .slice(0, 7)
  }, [current])

  // const maxScore = Math.max(...rankedStudents.map((s) => s.score), 0)
  // const minScore = Math.min(...rankedStudents.map((s) => s.score), 100)

  const minHeight = 120

  const scaledStudents: Array<ScaledStudent> = rankedStudents.map((student) => {
    let heightValue = 0
    if (student.realRank === 1) {
      heightValue = minHeight + 70
    } else if (student.realRank === 2) {
      heightValue = minHeight + 45
    } else if (student.realRank === 3) {
      heightValue = minHeight + 30
    } else {
      // Dynamic height for the rest, starting from 80px down
      heightValue = minHeight
    }

    return { ...student, height: `${heightValue}px` }
  })

  const reordered = reorderFancyPodium(scaledStudents)
  return reordered
}

const reorderFancyPodium = (students: Array<ScaledStudent>) => {
  if (students.length === 0) return [] as Array<ScaledStudent>
  const center = students[0]
  const left: Array<ScaledStudent> = []
  const right: Array<ScaledStudent> = []
  const rest = students.slice(1)

  rest.forEach((student, index) => {
    if (index % 2 === 0) {
      right.push(student)
    } else {
      left.unshift(student)
    }
  })

  return [...left, center, ...right]
}

const homeBackGround = (
  <svg
    width="100%"
    height="283"
    viewBox="0 0 100% 283"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_1427_3668)">
      <rect width="100%" height="283" fill="#DFE5EF" />
      <circle cx="1034" cy="-45" r="250" fill="white" fillOpacity="0.3" />
      <circle cx="10" cy="273" r="100" fill="white" fillOpacity="0.3" />
      <circle cx="30" cy="43" r="20" fill="white" fillOpacity="0.3" />
    </g>
  </svg>
)
